import { apiRequest, APIError } from '../../lib/api';
import type { ClassroomQuizQuestion } from '../../types/sync';
export interface SegmentSummary { title: string; summary: string; bulletPoints: string[]; keywords: string[] }
export interface FullSummary { title: string; overview: string; sections: Array<{ title: string; body: string; sourceIDs: string[] }>; corrections: string[]; excluded: string[] }
export interface FullSummaryInput { title: string; outputLanguage: string; sections: Array<Pick<SegmentSummary, 'title' | 'summary' | 'bulletPoints'> & { id: string; sourceText: string }> }
export interface ClassroomAI {
  polish(text: string, language: string, signal: AbortSignal): Promise<string>;
  summarize(text: string, language: string, signal: AbortSignal): Promise<SegmentSummary>;
  quiz(input: { blockTitle: string; blockSummary: string; bulletPoints: string[]; keywords: string[]; outputLanguage: string }, signal: AbortSignal): Promise<{ title: string; difficulty: number; questions: Array<Pick<ClassroomQuizQuestion, 'type' | 'stem' | 'options' | 'correctAnswer' | 'explanation'>> }>;
  full(input: FullSummaryInput, signal: AbortSignal): Promise<FullSummary>;
}
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');
export function validateSummary(value: SegmentSummary) {
  if (!value || typeof value.title !== 'string' || typeof value.summary !== 'string' || !value.summary.trim() || !strings(value.bulletPoints) || !strings(value.keywords)) throw new Error('整理结果格式无效，请重试。');
  return value;
}
export function validateFull(value: FullSummary, input: FullSummaryInput) {
  const ids = new Set(input.sections.map(section => section.id.toLowerCase()));
  if (!value || typeof value.title !== 'string' || typeof value.overview !== 'string' || !value.overview.trim() || !Array.isArray(value.sections) || !strings(value.corrections) || !strings(value.excluded) || value.sections.some(section => typeof section.title !== 'string' || typeof section.body !== 'string' || !strings(section.sourceIDs) || !section.sourceIDs.length || section.sourceIDs.some(id => !ids.has(id.toLowerCase())))) throw new Error('完整总结的来源引用无效，请重试。');
  return value;
}
const post = <T>(path: string, input: unknown, signal: AbortSignal) => apiRequest<T>(`/v1/llm/${path}`, { method: 'POST', body: JSON.stringify(input), signal });
export const classroomAI: ClassroomAI = {
  async polish(text, outputLanguage, signal) {
    const result = await post<{ polishedTranscript: string }>('polish-transcript', { text, outputLanguage }, signal);
    if (typeof result.polishedTranscript !== 'string' || !result.polishedTranscript.trim()) throw new Error('原文重整结果为空，请重试。');
    return result.polishedTranscript;
  },
  async summarize(text, outputLanguage, signal) { return validateSummary(await post<SegmentSummary>('summarize', { text, outputLanguage }, signal)); },
  quiz: (input, signal) => post('quiz', input, signal),
  async full(input, signal) { return validateFull(await post<FullSummary>('classroom-summary', input, signal), input); },
};
export function processingError(error: unknown) {
  if (error instanceof APIError) {
    if (error.status === 401 || error.status === 403) return '请登录有效账号后重试整理。';
    if (error.status === 402) return '积分不足，已保留原文和处理进度。';
    if (error.status === 429) return '请求较多，请稍后重试；已保留处理进度。';
  }
  return '整理暂未完成，请检查网络后重试；原文和已完成结果均已保留。';
}
export function outputLanguage() {
  const tag = navigator.language.toLowerCase();
  if (tag.startsWith('zh')) return /tw|hk|hant/.test(tag) ? 'zh-Hant' : 'zh-Hans';
  return ['en', 'ja', 'ko', 'fr', 'de', 'es'].find(language => tag.startsWith(language)) ?? 'en';
}
